import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useNavigate, useParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import api from '../api/api';
import { useUser } from '../context/UserContext';
import 'react-datepicker/dist/react-datepicker.css';
import './directBooking.css';

const stripePublishableKey = String(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : Promise.resolve(null);
const stripeAppearance = {
    theme: 'stripe',
    variables: {
        colorPrimary: '#0f172a',
        colorText: '#0f172a',
        colorDanger: '#b91c1c',
        borderRadius: '8px'
    }
};

const ReservationFormShell = ({
    clientSecret,
    form,
    nights,
    pricing,
    nightlyBreakdown,
    listing,
    minNights,
    calendarStartDate,
    calendarEndDate,
    blockedDateObjects,
    blockedDateSet,
    pricingRules,
    message,
    submitting,
    paymentInitializing,
    onChange,
    onDateRangeChange,
    onSubmit,
    paymentContent
}) => {
    const formTopRef = useRef(null);

    useEffect(() => {
        if (message.type === 'error' && message.text) {
            formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [message]);

    return (
        <form className="public-booking-card public-booking-reserve-card" onSubmit={onSubmit}>
            <div ref={formTopRef} />
            {message.text && <div className={`direct-booking-alert ${message.type === 'success' ? 'direct-booking-alert-success' : 'direct-booking-alert-error'}`}>{message.text}</div>}

            <div className="public-booking-cal-wrap">
                <DatePicker
                    inline
                    selectsRange
                    startDate={calendarStartDate}
                    endDate={calendarEndDate}
                    onChange={onDateRangeChange}
                    minDate={new Date()}
                    excludeDates={blockedDateObjects}
                    dayClassName={(date) => {
                        const key = toLocalDateKey(date);
                        if (blockedDateSet.has(key)) return 'booking-day-blocked';
                        return '';
                    }}
                    renderDayContents={(dayOfMonth, date) => {
                        const key = toLocalDateKey(date);
                        const rule = pricingRules.find((r) => key >= r.startDate && key < r.endDate);
                        const nightlyRate = rule ? Number(rule.nightlyRate) : Number(listing?.nightlyRate || 0);
                        return (
                            <>
                                <span className="booking-day-num">{dayOfMonth}</span>
                                <span className="booking-day-price">${Math.round(nightlyRate)}</span>
                            </>
                        );
                    }}
                />
            </div>

            <div className="public-booking-cal-legend">
                <span className="cal-legend-item"><span className="cal-legend-swatch cal-legend-blocked" />Unavailable</span>
                <span className="cal-legend-item"><span className="cal-legend-swatch cal-legend-open" />Available</span>
            </div>
            {minNights > 1 && <p className="public-booking-minstay">Minimum stay: {minNights} night{minNights !== 1 ? 's' : ''}</p>}

            <div className="public-booking-date-chips">
                <div className={`public-booking-date-chip ${form.startDate ? 'is-set' : ''}`}>
                    <span className="date-chip-label">CHECK-IN</span>
                    <span className="date-chip-value">{form.startDate || '-'}</span>
                </div>
                <span className="date-chip-arrow">to</span>
                <div className={`public-booking-date-chip ${form.endDate ? 'is-set' : ''}`}>
                    <span className="date-chip-label">CHECK-OUT</span>
                    <span className="date-chip-value">{form.endDate || '-'}</span>
                </div>
            </div>
            {nights > 0 && <p className="public-booking-nights-badge">{nights} night{nights !== 1 ? 's' : ''} selected</p>}

            <div className="public-booking-section-divider">Guest Details</div>
            <label>Full name<input name="guestName" value={form.guestName} onChange={onChange} placeholder="Jane Smith" required /></label>
            <label>Email address<input name="guestEmail" type="email" value={form.guestEmail} onChange={onChange} placeholder="jane@example.com" required readOnly /></label>
            <label>Phone <span className="public-booking-optional">(optional)</span><input name="guestPhone" value={form.guestPhone} onChange={onChange} placeholder="+1 555 000 0000" /></label>

            <div className="public-booking-section-divider">Payment Details</div>
            <div className="public-booking-card-field">
                {paymentContent}
            </div>

            {nightlyBreakdown.length > 0 && (
                <div className="public-booking-pricing">
                    {(() => {
                        const groups = [];
                        nightlyBreakdown.forEach((item) => {
                            const last = groups[groups.length - 1];
                            if (last && last.rate === item.rate && last.label === item.label) {
                                last.count += 1;
                            } else {
                                groups.push({ rate: item.rate, label: item.label, count: 1 });
                            }
                        });
                        return groups.map((g, i) => (
                            <p key={i}>
                                <span>{currency(g.rate)} x {g.count} night{g.count !== 1 ? 's' : ''}{g.label ? <em> ({g.label})</em> : ''}</span>
                                <strong>{currency(g.rate * g.count)}</strong>
                            </p>
                        ));
                    })()}
                    {pricing.cleaningFee > 0 && <p><span>Cleaning fee</span><strong>{currency(pricing.cleaningFee)}</strong></p>}
                    <p className="public-booking-total"><span>Total</span><strong>{currency(pricing.total)}</strong></p>
                </div>
            )}

            <button className="btn-primary public-booking-reserve-btn" type="submit" disabled={submitting || paymentInitializing || !form.startDate || !form.endDate || !clientSecret || !stripePublishableKey}>
                {submitting ? 'Processing...' : paymentInitializing ? 'Preparing payment...' : nights > 0 ? `Pay & Reserve - ${currency(pricing.total)}` : 'Select dates to reserve'}
            </button>
            <p className="public-booking-charge-note">Your card will be charged before the reservation is confirmed. Apple Pay, Google Pay, Link, and saved methods appear when available.</p>
            {!stripePublishableKey && <p className="public-booking-charge-note">Payments are unavailable because Stripe is not configured for this site yet.</p>}
        </form>
    );
};

const ReservationCheckoutForm = ({
    clientSecret,
    form,
    nights,
    pricing,
    nightlyBreakdown,
    listing,
    minNights,
    calendarStartDate,
    calendarEndDate,
    blockedDateObjects,
    blockedDateSet,
    pricingRules,
    message,
    submitting,
    paymentInitializing,
    onChange,
    onDateRangeChange,
    onValidate,
    onReservationSuccess
}) => {
    const stripe = useStripe();
    const elements = useElements();

    const reserve = async (event) => {
        event.preventDefault();

        try {
            await onValidate();
        } catch (e) {
            onReservationSuccess(null, e);
            return;
        }

        if (!stripe || !elements || !clientSecret) {
            onReservationSuccess(null, new Error('Payment form is still loading.'));
            return;
        }

        const result = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: {
                payment_method_data: {
                    billing_details: {
                        name: form.guestName,
                        email: form.guestEmail,
                        phone: form.guestPhone || undefined
                    }
                }
            }
        });

        if (result.error) {
            onReservationSuccess(null, new Error(result.error.message || 'Payment failed. Please review your payment details.'));
            return;
        }

        if (!result.paymentIntent || result.paymentIntent.status !== 'succeeded') {
            onReservationSuccess(null, new Error('Payment could not be completed. Please try again.'));
            return;
        }

        onReservationSuccess(result.paymentIntent.id, null);
    };

    return (
        <ReservationFormShell
            clientSecret={clientSecret}
            form={form}
            nights={nights}
            pricing={pricing}
            nightlyBreakdown={nightlyBreakdown}
            listing={listing}
            minNights={minNights}
            calendarStartDate={calendarStartDate}
            calendarEndDate={calendarEndDate}
            blockedDateObjects={blockedDateObjects}
            blockedDateSet={blockedDateSet}
            pricingRules={pricingRules}
            message={message}
            submitting={submitting}
            paymentInitializing={paymentInitializing}
            onChange={onChange}
            onDateRangeChange={onDateRangeChange}
            onSubmit={reserve}
            paymentContent={clientSecret ? <PaymentElement /> : <p className="public-booking-payment-loading">Preparing secure checkout...</p>}
        />
    );
};

const parseDate = (value) => {
    if (!value) return null;
    const normalized = String(value).slice(0, 10);
    const parts = normalized.split('-');
    if (parts.length !== 3) return null;
    const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return Number.isNaN(date.getTime()) ? null : date;
};

const parseLocalDate = (value) => {
    if (!value) return null;
    const parts = String(value).slice(0, 10).split('-');
    if (parts.length !== 3) return null;
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return Number.isNaN(date.getTime()) ? null : date;
};

const nightsBetween = (start, end) => {
    if (!start || !end || end <= start) return 0;
    return Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
};

const currency = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const toDateKey = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toLocalDateKey = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const calcDynamicSubtotal = (startDateStr, endDateStr, baseRate, pricingRules) => {
    if (!startDateStr || !endDateStr) return 0;
    const items = [];
    const start = parseDate(startDateStr);
    const end = parseDate(endDateStr);
    if (!start || !end || end <= start) return [];
    for (let current = new Date(start); current < end; current.setDate(current.getDate() + 1)) {
        const key = toDateKey(current);
        const rule = pricingRules.find((r) => key >= r.startDate && key < r.endDate);
        items.push({ date: key, rate: rule ? Number(rule.nightlyRate) : Number(baseRate), label: rule?.label || null });
    }
    return items;
};

const PublicBookingPageContent = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user, setUser } = useUser();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [paymentInitializing, setPaymentInitializing] = useState(false);
    const [paymentClientSecret, setPaymentClientSecret] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [blockedDates, setBlockedDates] = useState([]);
    const [selectedGalleryImage, setSelectedGalleryImage] = useState('');
    const [showReserveModal, setShowReserveModal] = useState(false);
    const [showAccountSetupModal, setShowAccountSetupModal] = useState(false);
    const [accountSubmitting, setAccountSubmitting] = useState(false);
    const [accountMessage, setAccountMessage] = useState({ type: '', text: '' });
    const [accountForm, setAccountForm] = useState({ password: '', confirmPassword: '' });
    const [form, setForm] = useState({
        startDate: '',
        endDate: '',
        guestName: '',
        guestEmail: '',
        guestPhone: '',
    });

    const nights = useMemo(() => nightsBetween(parseDate(form.startDate), parseDate(form.endDate)), [form.startDate, form.endDate]);
    const calendarStartDate = useMemo(() => parseLocalDate(form.startDate), [form.startDate]);
    const calendarEndDate = useMemo(() => parseLocalDate(form.endDate), [form.endDate]);
    const blockedDateObjects = useMemo(() => blockedDates.map((value) => parseDate(value)).filter(Boolean), [blockedDates]);
    const blockedDateSet = useMemo(() => new Set(blockedDates), [blockedDates]);
    const minNights = useMemo(() => Number(listing?.minNights) || 1, [listing]);
    const pricingRules = useMemo(() => Array.isArray(listing?.pricingRules) ? listing.pricingRules : [], [listing]);
    const galleryEntries = useMemo(() => {
        const entries = [];
        (listing?.gallery || []).forEach((item) => {
            if (typeof item === 'string') {
                const normalized = item.trim();
                if (normalized && !entries.some((entry) => entry.url === normalized)) {
                    entries.push({ url: normalized, label: '' });
                }
                return;
            }

            const url = String(item?.url || '').trim();
            const label = String(item?.label || '').trim();
            if (url && !entries.some((entry) => entry.url === url)) {
                entries.push({ url, label });
            }
        });
        return entries;
    }, [listing]);

    const galleryImages = useMemo(() => galleryEntries.map((entry) => entry.url), [galleryEntries]);
    const activeGalleryImage = useMemo(() => {
        if (selectedGalleryImage && galleryImages.includes(selectedGalleryImage)) return selectedGalleryImage;
        return galleryImages[0] || '';
    }, [selectedGalleryImage, galleryImages]);
    const activeGalleryLabel = useMemo(() => {
        const entry = galleryEntries.find((item) => item.url === activeGalleryImage);
        return entry?.label || '';
    }, [activeGalleryImage, galleryEntries]);
    const nightlyBreakdown = useMemo(() => {
        if (!form.startDate || !form.endDate) return [];
        return calcDynamicSubtotal(form.startDate, form.endDate, listing?.nightlyRate || 0, pricingRules);
    }, [form.startDate, form.endDate, listing, pricingRules]);

    const pricing = useMemo(() => {
        const cleaningFee = Number(listing?.cleaningFee) || 0;
        const subtotal = nightlyBreakdown.reduce((acc, item) => acc + item.rate, 0);
        return {
            cleaningFee,
            subtotal,
            total: subtotal + cleaningFee
        };
    }, [listing, nightlyBreakdown]);

    const load = async () => {
        setLoading(true);
        try {
            const [listingResponse, blockedDatesResponse] = await Promise.all([
                api.get(`/direct-booking/listings/${slug}`),
                api.get(`/direct-booking/listings/${slug}/blocked-dates`)
            ]);
            setListing(listingResponse.data);
            setBlockedDates(Array.isArray(blockedDatesResponse?.data?.blockedDates) ? blockedDatesResponse.data.blockedDates : []);
        } catch (e) {
            setMessage({ type: 'error', text: 'Unable to load this property listing.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    useEffect(() => {
        setSelectedGalleryImage('');
        setShowReserveModal(false);
        setShowAccountSetupModal(false);
    }, [slug]);

    useEffect(() => {
        if (!user) return;

        setForm((prev) => ({
            ...prev,
            guestName: prev.guestName || String(user.display || '').trim(),
            guestEmail: prev.guestEmail || String(user.email || '').trim().toLowerCase()
        }));
    }, [user]);

    const onChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const onAccountChange = (event) => {
        const { name, value } = event.target;
        setAccountForm((prev) => ({ ...prev, [name]: value }));
    };

    const validateReservationRequest = useCallback(async () => {
        if (!form.guestName.trim() || !form.guestEmail.trim()) {
            throw new Error('Guest name and email are required.');
        }

        if (user && String(form.guestEmail || '').trim().toLowerCase() !== String(user.email || '').trim().toLowerCase()) {
            throw new Error('Please use your logged-in account email for this booking.');
        }

        if (nights <= 0) {
            throw new Error('Choose a valid date range.');
        }

        const rangeHasBlockedDates = (() => {
            const start = parseDate(form.startDate);
            const end = parseDate(form.endDate);
            if (!start || !end || end <= start) return true;

            for (let current = new Date(start); current < end; current = new Date(current.getTime() + 86400000)) {
                if (blockedDateSet.has(toDateKey(current))) return true;
            }
            return false;
        })();

        if (rangeHasBlockedDates) {
            throw new Error('Selected dates include unavailable nights. Please choose open dates.');
        }

        const availability = await api.get(`/direct-booking/listings/${slug}/availability?startDate=${form.startDate}&endDate=${form.endDate}`);
        if (!availability?.data?.available) {
            throw new Error('Those dates are no longer available. Please choose different dates.');
        }
    }, [blockedDateSet, form.endDate, form.guestEmail, form.guestName, form.startDate, nights, slug, user]);

    const submitReservation = async (paymentIntentId) => {
        const response = await api.post(`/direct-booking/listings/${slug}/reserve`, {
            startDate: form.startDate,
            endDate: form.endDate,
            guestName: form.guestName,
            guestEmail: form.guestEmail,
            guestPhone: form.guestPhone,
            paymentIntentId
        });

        navigate(`/guest-chat/${response.data.conversationId}?token=${encodeURIComponent(response.data.guestAccessToken)}`);
    };

    const onDateRangeChange = (dates) => {
        const [start, end] = dates || [];
        const startKey = start ? toLocalDateKey(start) : '';
        const endKey = end ? toLocalDateKey(end) : '';

        if (startKey && endKey) {
            const startUtc = parseDate(startKey);
            const endUtc = parseDate(endKey);
            const selectedNights = nightsBetween(startUtc, endUtc);
            if (selectedNights < minNights) {
                setForm((prev) => ({ ...prev, startDate: startKey, endDate: '' }));
                setMessage({ type: 'error', text: `Minimum stay is ${minNights} night${minNights !== 1 ? 's' : ''}. Please choose a longer range.` });
                return;
            }
        }

        setMessage({ type: '', text: '' });
        setForm((prev) => ({ ...prev, startDate: startKey, endDate: endKey }));
    };

    useEffect(() => {
        const initializePaymentIntent = async () => {
            if (!showReserveModal || !user || !form.startDate || !form.endDate || !form.guestEmail || !stripePublishableKey) {
                setPaymentClientSecret('');
                setPaymentInitializing(false);
                return;
            }

            setPaymentInitializing(true);
            try {
                await validateReservationRequest();
                const intentResponse = await api.post(`/direct-booking/listings/${slug}/payment-intent`, {
                    startDate: form.startDate,
                    endDate: form.endDate,
                    guestEmail: form.guestEmail
                });

                const clientSecret = String(intentResponse?.data?.clientSecret || '');
                if (!clientSecret) {
                    throw new Error('Unable to initialize payment.');
                }

                setPaymentClientSecret(clientSecret);
            } catch (e) {
                setPaymentClientSecret('');
                const backendError = e?.response?.data?.error;
                setMessage({ type: 'error', text: backendError || e.message || 'Unable to initialize payment.' });
            } finally {
                setPaymentInitializing(false);
            }
        };

        initializePaymentIntent();
    }, [form.endDate, form.guestEmail, form.startDate, showReserveModal, slug, user, validateReservationRequest]);

    const handleReservationSuccess = async (confirmedPaymentIntentId, error) => {
        if (error) {
            setMessage({ type: 'error', text: error.message || 'Unable to complete this reservation right now.' });
            setSubmitting(false);
            return;
        }

        if (!confirmedPaymentIntentId) {
            setMessage({ type: 'error', text: 'Payment confirmation is missing.' });
            setSubmitting(false);
            return;
        }

        setSubmitting(true);
        setMessage({ type: '', text: '' });

        try {
            await submitReservation(confirmedPaymentIntentId);
        } catch (e) {
            const backendError = e?.response?.data?.error;
            setMessage({ type: 'error', text: backendError || e.message || 'Unable to complete this reservation right now.' });
        } finally {
            setSubmitting(false);
        }
    };

    const createAccountAndReserve = async (event) => {
        event.preventDefault();
        const password = String(accountForm.password || '');
        const confirmPassword = String(accountForm.confirmPassword || '');

        if (password.length < 8) {
            setAccountMessage({ type: 'error', text: 'Password must be at least 8 characters.' });
            return;
        }

        if (password !== confirmPassword) {
            setAccountMessage({ type: 'error', text: 'Passwords do not match.' });
            return;
        }

        setAccountSubmitting(true);
        setAccountMessage({ type: '', text: '' });
        setMessage({ type: '', text: '' });

        try {
            await validateReservationRequest();

            const accountResponse = await api.post('/user/direct-booking-account', {
                display: form.guestName,
                email: form.guestEmail,
                password
            });

            setUser(accountResponse.data);
            localStorage.setItem('user', JSON.stringify(accountResponse.data));
            setShowAccountSetupModal(false);
            setAccountMessage({ type: 'success', text: 'Account created. Complete payment to confirm your stay.' });
        } catch (e) {
            const backendError = e?.response?.data?.error;
            setAccountMessage({ type: 'error', text: backendError || e.message || 'Unable to complete your booking right now.' });
        } finally {
            setAccountSubmitting(false);
        }
    };

    if (loading) {
        return <div className="direct-booking-page"><p>Loading listing...</p></div>;
    }

    if (!listing) {
        return <div className="direct-booking-page"><p>This listing is not available.</p></div>;
    }

    return (
        <div className="direct-booking-page public-booking-page">
            <section className="public-booking-hero" style={{ backgroundImage: listing.heroImageUrl ? `url(${listing.heroImageUrl})` : 'none' }}>
                <div className="public-booking-hero-overlay">
                    <h1>{listing.title}</h1>
                    <p>{listing.shortDescription}</p>
                    <span>{listing.property?.city}, {listing.property?.state}{listing.property?.neighborhood ? ` - ${listing.property.neighborhood}` : ''}</span>
                    <div className="public-booking-hero-stats">
                        <div className="public-booking-hero-stat">
                            <strong>{Number(listing.property?.bedrooms) || 0}</strong>
                            <span>{Number(listing.property?.bedrooms) === 1 ? 'Bedroom' : 'Bedrooms'}</span>
                        </div>
                        <div className="public-booking-hero-stat">
                            <strong>{Number(listing.property?.bathrooms) || 0}</strong>
                            <span>{Number(listing.property?.bathrooms) === 1 ? 'Bathroom' : 'Bathrooms'}</span>
                        </div>
                    </div>
                    <div className="public-booking-hero-reserve">
                        <h3>Reserve This Stay</h3>
                        <p>Choose your dates, enter guest details, and complete payment in checkout.</p>
                        {listing.property && (
                            <p className="public-booking-reserve-meta">{listing.property.city}, {listing.property.state}</p>
                        )}
                        {nights > 0 && <p className="public-booking-nights-badge">{nights} night{nights !== 1 ? 's' : ''} selected</p>}
                        <button className="btn-primary public-booking-open-modal-btn" type="button" onClick={() => setShowReserveModal(true)}>
                            {nights > 0 ? `Continue to Reserve · ${currency(pricing.total)}` : 'Select Dates & Reserve'}
                        </button>
                    </div>
                </div>
            </section>

            <section className="public-booking-card public-booking-gallery-section">
                <h3>Photo Tour</h3>
                {galleryImages.length > 0 ? (
                    <div className="public-booking-gallery-showcase">
                        <div className="public-booking-gallery-main">
                            <img src={activeGalleryImage} alt={activeGalleryLabel || `${listing.title} view`} />
                        </div>
                        {activeGalleryLabel && <p className="public-booking-gallery-caption">{activeGalleryLabel}</p>}
                        <div className="public-booking-gallery-strip" aria-label="Listing photo thumbnails">
                            {galleryEntries.slice(0, 8).map((entry, index) => {
                                const isActive = entry.url === activeGalleryImage;
                                const thumbLabel = String(entry.label || '').trim();
                                return (
                                    <button
                                        key={`${entry.url}-${index}`}
                                        type="button"
                                        className={`public-booking-gallery-thumb ${isActive ? 'is-active' : ''}`}
                                        onClick={() => setSelectedGalleryImage(entry.url)}
                                        aria-label={thumbLabel ? `Show ${thumbLabel}` : 'Show listing photo'}
                                        aria-pressed={isActive}
                                    >
                                        <img src={entry.url} alt="" />
                                        {thumbLabel && <span className="public-booking-gallery-thumb-label">{thumbLabel}</span>}
                                    </button>
                                );
                            })}
                        </div>
                        {galleryEntries.length > 8 && <p className="public-booking-gallery-more">+{galleryEntries.length - 8} more photos</p>}
                    </div>
                ) : (
                    <p>No gallery images yet.</p>
                )}
            </section>

            <main className="public-booking-main">
                <section className="public-booking-card">
                    <h3>About this stay</h3>
                    <p>{listing.longDescription || 'No description provided yet.'}</p>
                </section>

                {listing.cancellationPolicy && (
                    <section className="public-booking-card">
                        <h3>Cancellation Policy</h3>
                        <div className="public-booking-cancellation-policy">
                            <div className="policy-tier">
                                <strong>Full Refund</strong>
                                <p>Cancel more than {listing.cancellationPolicy.fullRefundDays} days before check-in for a full refund</p>
                            </div>
                            <div className="policy-tier">
                                <strong>Partial Refund ({listing.cancellationPolicy.partialRefundPercent}%)</strong>
                                <p>Cancel {listing.cancellationPolicy.partialRefundDays}-{listing.cancellationPolicy.fullRefundDays} days before check-in for a {listing.cancellationPolicy.partialRefundPercent}% refund</p>
                            </div>
                            <div className="policy-tier">
                                <strong>No Refund</strong>
                                <p>Cancel within {listing.cancellationPolicy.partialRefundDays} day{listing.cancellationPolicy.partialRefundDays !== 1 ? 's' : ''} of check-in with no refund</p>
                            </div>
                        </div>
                    </section>
                )}

                <div className="public-booking-two-col">
                <section className="public-booking-card">
                    <h3>Amenities</h3>
                    <ul className="public-booking-amenities">
                        {(listing.amenities || []).map((amenity) => <li key={amenity}>{amenity}</li>)}
                        {(listing.amenities || []).length === 0 && <li>Amenities will be listed soon.</li>}
                    </ul>
                </section>

                <section className="public-booking-card">
                    <h3>House Rules</h3>
                    <p>{listing.houseRules || 'Please respect the home and neighbors.'}</p>
                </section>
                </div>
            </main>

            {showReserveModal && (
                <div className="public-booking-modal-overlay" onClick={() => setShowReserveModal(false)}>
                    <div className="public-booking-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="public-booking-modal-header">
                            <h3>Reserve and Pay</h3>
                            <button type="button" className="public-booking-modal-close" onClick={() => setShowReserveModal(false)}>&times;</button>
                        </div>
                        <div className="public-booking-modal-body">
                            {!user && (
                                <div className="public-booking-card public-booking-account-card">
                                    {message.text && <div className={`direct-booking-alert ${message.type === 'success' ? 'direct-booking-alert-success' : 'direct-booking-alert-error'}`}>{message.text}</div>}
                                    <p className="public-booking-account-copy">Create or log into a guest account before entering payment details and confirming this reservation.</p>
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        onClick={async () => {
                                            try {
                                                setMessage({ type: '', text: '' });
                                                await validateReservationRequest();
                                                setShowAccountSetupModal(true);
                                            } catch (e) {
                                                const backendError = e?.response?.data?.error;
                                                setMessage({ type: 'error', text: backendError || e.message || 'Unable to continue with this reservation.' });
                                            }
                                        }}
                                    >
                                        Continue to Account Setup
                                    </button>
                                </div>
                            )}

                            {user && paymentClientSecret && (
                                <Elements stripe={stripePromise} options={{ clientSecret: paymentClientSecret, appearance: stripeAppearance }}>
                                    <ReservationCheckoutForm
                                        clientSecret={paymentClientSecret}
                                        form={form}
                                        nights={nights}
                                        pricing={pricing}
                                        nightlyBreakdown={nightlyBreakdown}
                                        listing={listing}
                                        minNights={minNights}
                                        calendarStartDate={calendarStartDate}
                                        calendarEndDate={calendarEndDate}
                                        blockedDateObjects={blockedDateObjects}
                                        blockedDateSet={blockedDateSet}
                                        pricingRules={pricingRules}
                                        message={message}
                                        submitting={submitting}
                                        paymentInitializing={paymentInitializing}
                                        onChange={onChange}
                                        onDateRangeChange={onDateRangeChange}
                                        onValidate={validateReservationRequest}
                                        onReservationSuccess={handleReservationSuccess}
                                    />
                                </Elements>
                            )}
                            {user && !paymentClientSecret && (
                                <ReservationFormShell
                                    clientSecret=""
                                    form={form}
                                    nights={nights}
                                    pricing={pricing}
                                    nightlyBreakdown={nightlyBreakdown}
                                    listing={listing}
                                    minNights={minNights}
                                    calendarStartDate={calendarStartDate}
                                    calendarEndDate={calendarEndDate}
                                    blockedDateObjects={blockedDateObjects}
                                    blockedDateSet={blockedDateSet}
                                    pricingRules={pricingRules}
                                    message={message}
                                    submitting={submitting}
                                    paymentInitializing={paymentInitializing}
                                    onChange={onChange}
                                    onDateRangeChange={onDateRangeChange}
                                    onSubmit={(event) => event.preventDefault()}
                                    paymentContent={(
                                        <p className="public-booking-payment-loading">
                                            {paymentInitializing
                                                ? 'Preparing secure checkout...'
                                                : stripePublishableKey
                                                    ? 'Select your stay dates and confirm guest details to load the secure payment form.'
                                                    : 'Payments are unavailable because Stripe is not configured for this site yet.'}
                                        </p>
                                    )}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showAccountSetupModal && (
                <div className="public-booking-modal-overlay" onClick={() => !accountSubmitting && setShowAccountSetupModal(false)}>
                    <div className="public-booking-modal public-booking-account-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="public-booking-modal-header">
                            <h3>Create Your Account</h3>
                            <button type="button" className="public-booking-modal-close" onClick={() => setShowAccountSetupModal(false)} disabled={accountSubmitting}>&times;</button>
                        </div>
                        <div className="public-booking-modal-body">
                            <div className="public-booking-card public-booking-account-card">
                                <p className="public-booking-account-copy">Create a password to save this reservation under {form.guestEmail || 'your email'} before we confirm the booking.</p>
                                {accountMessage.text && <div className={`direct-booking-alert ${accountMessage.type === 'success' ? 'direct-booking-alert-success' : 'direct-booking-alert-error'}`}>{accountMessage.text}</div>}
                                <form className="public-booking-account-form" onSubmit={createAccountAndReserve}>
                                    <label>
                                        Password
                                        <input name="password" type="password" value={accountForm.password} onChange={onAccountChange} placeholder="Create a password" required />
                                    </label>
                                    <label>
                                        Confirm password
                                        <input name="confirmPassword" type="password" value={accountForm.confirmPassword} onChange={onAccountChange} placeholder="Confirm your password" required />
                                    </label>
                                    <button className="btn-primary" type="submit" disabled={accountSubmitting}>
                                        {accountSubmitting ? 'Creating account...' : 'Create Account and Continue'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicBookingPageContent;
